(() => {
  const SECTION_SELECTOR = '.sec__pacifiq-community';

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class PacifiqCommunitySection {
    constructor(section) {
      this.section = section;
      this.steps = section.querySelector('[data-pc-steps]');
      this.stepDots = Array.from(section.querySelectorAll('[data-pc-step-dot]'));
      this.parallaxImage = section.querySelector('[data-pc-parallax-image]');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.parallaxStrength = Number(section.dataset.parallaxStrength || 36);
      this.scrollTicking = false;
      this.resizeTicking = false;
      this.metrics = {
        lineStart: 0,
        lineEnd: 0,
      };

      if (!this.steps || this.stepDots.length < 2) {
        return;
      }

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.measure();
      this.update();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
    }

    measure() {
      const firstDot = this.stepDots[0];
      const lastDot = this.stepDots[this.stepDots.length - 1];
      const stepsRect = this.steps.getBoundingClientRect();
      const firstRect = firstDot.getBoundingClientRect();
      const lastRect = lastDot.getBoundingClientRect();

      this.metrics.lineStart = firstRect.top - stepsRect.top + firstRect.height * 0.5;
      this.metrics.lineEnd = lastRect.top - stepsRect.top + lastRect.height * 0.5;

      this.steps.style.setProperty('--pc-line-start', `${this.metrics.lineStart}px`);
      this.steps.style.setProperty('--pc-line-end', `${this.metrics.lineEnd}px`);
    }

    onScroll() {
      if (this.scrollTicking) return;

      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.update();
        this.scrollTicking = false;
      });
    }

    onResize() {
      if (this.resizeTicking) return;

      this.resizeTicking = true;
      window.requestAnimationFrame(() => {
        this.measure();
        this.update();
        this.resizeTicking = false;
      });
    }

    update() {
      const stepsRect = this.steps.getBoundingClientRect();
      const viewportFocus = window.innerHeight * 0.55;

      const start = stepsRect.top + this.metrics.lineStart;
      const end = stepsRect.top + this.metrics.lineEnd;
      const distance = Math.max(end - start, 1);
      const progress = clamp((viewportFocus - start) / distance, 0, 1);

      this.section.style.setProperty('--pc-line-progress', progress.toFixed(4));

      if (!this.parallaxImage || this.reduceMotion) return;

      const sectionRect = this.section.getBoundingClientRect();
      const centerDelta = window.innerHeight * 0.5 - (sectionRect.top + sectionRect.height * 0.5);
      const normalized = clamp(centerDelta / (window.innerHeight * 0.9), -1, 1);
      const translateY = -normalized * this.parallaxStrength;

      this.parallaxImage.style.transform = `translate3d(0, ${translateY.toFixed(2)}px, 0)`;
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }
  }

  const instances = new WeakMap();

  const initWithin = (scope = document) => {
    scope.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (instances.has(section)) return;
      instances.set(section, new PacifiqCommunitySection(section));
    });
  };

  const destroyWithin = (scope = document) => {
    scope.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      const instance = instances.get(section);
      if (!instance) return;
      instance.destroy();
      instances.delete(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin());
  } else {
    initWithin();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target) return;
    initWithin(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target) return;
    destroyWithin(event.target);
  });
})();
